import { useMemo } from "react"
import { useContentSetsMetadata } from "resources/content-sets"
import { OK, ERROR } from "helpers/data-statuses"
import { DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID } from "../modules/constants"

interface ThematicBetasContentSetResult {
  /** True once metadata loaded AND the account is entitled. False while loading, on error, or unentitled. */
  hasThematicBetasContentSet: boolean
  /** True once the content-sets request reached a *terminal* state (OK or ERROR) — so callers
   *  can stop deferring decisions that depend on the entitlement (e.g. seeding default panels).
   *  On ERROR we fall through with `hasThematicBetasContentSet: false` rather than hanging
   *  the dashboard waiting for metadata that will never arrive. */
  isContentSetsLoaded: boolean
}

/**
 * Used by useSyncPanelsPreferences (to defer seeding default panels until the
 * entitlement is known, avoiding a flash of five defaults that the renderer
 * would later supplement with the thematic-betas panel) and by
 * DashboardModulesRenderer (to inject the thematic-betas panel into the
 * default-snapshot layout).
 */
export function useHasThematicBetasContentSet(): ThematicBetasContentSetResult {
  const [contentSetsMetadata, contentSetsMetadataStatus] = useContentSetsMetadata()

  return useMemo(() => {
    const isContentSetsLoaded = contentSetsMetadataStatus === OK || contentSetsMetadataStatus === ERROR
    const hasThematicBetasContentSet = contentSetsMetadataStatus === OK && contentSetsMetadata
      ? contentSetsMetadata.some(({ id }: { id: string }) => id === DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID)
      : false
    return { hasThematicBetasContentSet, isContentSetsLoaded }
  }, [contentSetsMetadata, contentSetsMetadataStatus])
}
